import { useState } from 'react';
import { FaBan, FaCheck, FaInfoCircle, FaLevelUpAlt, FaRegCommentDots } from 'react-icons/fa';
import { Textarea } from '../../../components/forms/Textarea';
import { applyChecklistRequirements } from '../constants';
import { usePasStore } from '../pasStore';
import type { UnderwritingDecision } from '../types';
import { formatCurrency, formatDateTime, relativeTime } from '../utils';
import { Button } from './Button';
import { Checklist } from './Checklist';
import { SlideOver } from './SlideOver';
import { PriorityBadge, RiskTierBadge, UnderwritingStatusBadge } from './StatusBadges';
import { ProgressBar } from './primitives';

const DECISION_META: Record<UnderwritingDecision['decision'], { label: string; color: string }> = {
  approved: { label: 'Approved', color: 'var(--color-success-600)' },
  declined: { label: 'Declined', color: 'var(--color-error-600)' },
  referred: { label: 'Referred', color: 'var(--color-info-600)' },
  info_requested: { label: 'Info requested', color: 'var(--color-warning-600)' },
};

interface Props {
  policyId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export const UnderwritingDecisionPanel: React.FC<Props> = ({ policyId, isOpen, onClose }) => {
  const policy = usePasStore((s) => s.policies.find((p) => p.id === policyId));
  const setChecklistItem = usePasStore((s) => s.setChecklistItem);
  const recordDecision = usePasStore((s) => s.recordDecision);
  const addNote = usePasStore((s) => s.addNote);
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState<UnderwritingDecision['decision'] | null>(null);

  if (!policy) return null;

  const uw = policy.underwriting;
  // UI override: only the first three items are required (see applyChecklistRequirements).
  const checklist = applyChecklistRequirements(uw.checklist);
  const requiredRemaining = checklist.filter((c) => c.required && !c.done).length;
  const uwNotes = policy.notes.filter((n) => n.category === 'underwriting');

  const act = async (decision: UnderwritingDecision['decision']) => {
    const text = rationale.trim();
    const finalRationale =
      text ||
      (decision === 'approved'
        ? 'Approved within rating guidelines.'
        : decision === 'referred'
          ? 'Referred to senior underwriting for review.'
          : decision === 'info_requested'
            ? 'Additional information requested from the broker.'
            : 'Declined.');
    setBusy(decision);
    try {
      await recordDecision(policy.id, decision, finalRationale);
      setRationale('');
      if (decision === 'info_requested') await addNote(policy.id, finalRationale, 'underwriting');
      if (decision === 'approved') onClose();
    } catch {
      // The store surfaces the error as a toast.
    } finally {
      setBusy(null);
    }
  };

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={`Underwriting · ${policy.policyNumber}`}
      subtitle={policy.insured.name}
      width="xl"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <Button variant="ghost" leftIcon={<FaLevelUpAlt className="h-3 w-3" />} loading={busy === 'referred'} onClick={() => act('referred')}>
            Refer
          </Button>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" leftIcon={<FaInfoCircle className="h-3 w-3" />} loading={busy === 'info_requested'} onClick={() => act('info_requested')}>
              Request info
            </Button>
            <Button variant="danger" leftIcon={<FaBan className="h-3 w-3" />} loading={busy === 'declined'} onClick={() => act('declined')}>
              Decline
            </Button>
            <Button
              variant="success"
              leftIcon={<FaCheck className="h-3 w-3" />}
              loading={busy === 'approved'}
              disabled={requiredRemaining > 0}
              title={requiredRemaining > 0 ? 'Complete all required checklist items first' : undefined}
              onClick={() => act('approved')}
            >
              Approve
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-2">
          <UnderwritingStatusBadge status={uw.status} />
          <PriorityBadge priority={uw.priority} />
          <RiskTierBadge tier={policy.risk.tier} />
          <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            Assigned to {uw.assignedTo ?? 'Unassigned'}
            {uw.slaDueAt && ` · SLA ${relativeTime(uw.slaDueAt)}`}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Risk score', value: `${policy.risk.score}/100` },
            { label: 'Loss ratio', value: `${policy.risk.lossRatioPct}%` },
            { label: 'Annual premium', value: formatCurrency(policy.annualPremium) },
          ].map((s) => (
            <div key={s.label} className="rounded-lg border p-3" style={{ borderColor: 'var(--color-border-light)' }}>
              <p className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>
                {s.label}
              </p>
              <p className="text-base font-bold" style={{ color: 'var(--color-text-primary)' }}>
                {s.value}
              </p>
            </div>
          ))}
        </div>

        <div>
          <h4 className="mb-2 text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>
            Risk score
          </h4>
          <ProgressBar value={policy.risk.score} accent={policy.risk.score >= 80 ? 'success' : policy.risk.score >= 65 ? 'info' : 'warning'} />
          <p className="mt-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            {policy.risk.summary}
          </p>
        </div>

        <div>
          <h4 className="mb-3 text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>
            Underwriting checklist
          </h4>
          <Checklist items={checklist} onToggle={(itemId, done) => void setChecklistItem(policy.id, itemId, done).catch(() => {})} />
        </div>

        {uw.conditions.length > 0 && (
          <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--color-warning-50)', borderColor: 'var(--color-warning-100)' }}>
            <h4 className="mb-1.5 text-sm font-bold" style={{ color: 'var(--color-warning-800)' }}>
              Conditions
            </h4>
            <ul className="list-inside list-disc space-y-1 text-sm" style={{ color: 'var(--color-warning-800)' }}>
              {uw.conditions.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <h4 className="mb-2 text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>
            Decision
          </h4>
          <Textarea
            label=""
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            rows={3}
            placeholder="Record your rationale (recorded to the audit timeline)…"
            fullWidth
          />
        </div>

        <div>
          <h4 className="mb-3 text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>
            Decision history
          </h4>
          {uw.decisions.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              No decisions recorded yet.
            </p>
          ) : (
            <ul className="space-y-3">
              {uw.decisions.map((d) => (
                <li key={d.id} className="rounded-lg border p-3" style={{ borderColor: 'var(--color-border-light)' }}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold" style={{ color: DECISION_META[d.decision].color }}>
                      {DECISION_META[d.decision].label}
                    </span>
                    <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }} title={formatDateTime(d.at)}>
                      {relativeTime(d.at)}
                    </span>
                  </div>
                  <p className="mt-1 text-sm" style={{ color: 'var(--color-text-primary)' }}>
                    {d.rationale}
                  </p>
                  <p className="mt-1 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    {d.by}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <h4 className="mb-2 flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>
            <FaRegCommentDots className="h-3.5 w-3.5" /> Internal notes
          </h4>
          {uwNotes.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              No internal underwriting notes.
            </p>
          ) : (
            <ul className="space-y-2">
              {uwNotes.map((n) => (
                <li key={n.id} className="rounded-lg p-3" style={{ backgroundColor: 'var(--color-hover-background)' }}>
                  <p className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                    {n.body}
                  </p>
                  <p className="mt-1 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    {n.author} · {relativeTime(n.createdAt)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </SlideOver>
  );
};
